import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPromoSocialSummaryQuery } from '../../../../redux/api/fymAPI';
import { ErrorViewProps } from '../../../common/views/ErrorView/props';
import { useFirstReadPoll } from '../../promote/hooks/useFirstReadPoll';
import { HomePeriodDays } from '../homePeriod';
import { socialRowState } from './socialSummary';
import { SocialSummaryChannel, SocialSummaryPost } from './socialSummaryTypes';

const NO_CHANNELS: SocialSummaryChannel[] = [];

export interface HomeSocialSummary {
  /** The connected channels and the channels that need Reconnect, in the platform order. */
  channels: SocialSummaryChannel[];
  /** The post with the most views of the period, or null. */
  bestPost: SocialSummaryPost | null;
  isLoading: boolean;
  error: ErrorViewProps['error'];
  retry: () => void;
  /**
   * True while the Social block shows, or loads towards, its "Connect your channels" card: the
   * read did not fail and it gave no channel. The Promote block then hides its own connect card,
   * so Home shows one connect card.
   */
  ownsConnectCard: boolean;
}

/** The social numbers of the artist over the Home period (`promo/social-summary`). */
export function useHomeSocialSummary(accountId: number, days: HomePeriodDays): HomeSocialSummary {
  // While a row shows "Reading your numbers", the summary is read again every few seconds.
  const [isReading, setIsReading] = useState(false);
  const pollingInterval = useFirstReadPoll(isReading);
  const { data, isLoading, error, refetch } = useGetPromoSocialSummaryQuery({ accountId, days }, { pollingInterval });
  const isReadingNow = useMemo(
    () => data?.channels.some((channel) => socialRowState(channel) === 'reading') ?? false,
    [data?.channels]
  );
  useEffect(() => {
    setIsReading(isReadingNow);
  }, [isReadingNow]);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return useMemo(() => {
    const channels = data?.channels ?? NO_CHANNELS;
    return {
      channels,
      bestPost: data?.bestPost ?? null,
      isLoading,
      error,
      retry,
      ownsConnectCard: error === undefined && channels.length === 0,
    };
  }, [data, error, isLoading, retry]);
}
